import { useEffect, useId, useRef, useState } from "react";
import { MAX_ZNAKOW_KOMENTARZA } from "@shared/recenzjeTypy.js";
import { OPOZNIENIE_TEKSTU_MS } from "../../hooks/useAutozapis.js";
import { useAkcjePrzegladu, useStatusZapisu } from "../../hooks/usePrzegladRecenzji.js";
import { tytulUwagi, type PozycjaUwagi, type WpisUwagi } from "../../lib/uwagiWypowiedzi.js";
import { Button } from "../ui/Button.js";
import { Field, Textarea } from "../ui/Field.js";
import { Select, type OpcjaSelect } from "../ui/Select.js";
import { Spinner } from "../ui/Spinner.js";
import { StatusZapisu } from "./StatusZapisu.js";

/**
 * Formularz uwagi recenzenta, otwarty tuz pod wypowiedzia: tresc, opcjonalny parametr i "Gotowe".
 *
 * Zapisuje sie sam (po przerwie w pisaniu, po wyjsciu z pola, od razu przy zmianie parametru), przez to samo pole
 * kolejki co cala reszta przegladu: nowa uwaga nie powstaje, dopoki nie ma tresci, pierwszy zapis tworzy ja, kolejne ja
 * zmieniaja. Dlatego NIE MA "Anuluj": uwaga zapisuje sie w trakcie pisania, wiec "anuluj" sugerowaloby cofniecie, ktorego
 * nie ma. Jest "Gotowe" (zamyka formularz po zapisie) i, przy uwadze, ktora juz istnieje, "Usun uwage".
 *
 * USUWANIE: wyjscie z pola (alt-tab, klik obok, klawiatura telefonu, okno potwierdzenia) NIGDY niczego nie usuwa ani nie
 * wysyla, a wyczyszczona tresc nie wychodzi na serwer (serwer zachowuje ostatnia niepusta). Uwage usuwa tylko decyzja
 * recenzenta w oknie potwierdzenia: "Usun uwage" albo "Gotowe" na zapisanej uwadze z pustym polem. "Anuluj" w oknie
 * niczego nie zmienia (po "Gotowe" zamyka formularz i zostawia karte z ostatnia zapisana trescia). "Gotowe" na nowej,
 * niezapisanej uwadze z pustym polem po prostu zamyka formularz. Gdy usuniecie jest zakolejkowane, pole jest tylko do
 * odczytu, lista parametrow wylaczona, a status mowi "Usuwam...": nic nie moze go po cichu zastapic.
 * Escape niczego nie odrzuca.
 *
 * Tresc pola zyje W STANIE TEGO KOMPONENTU, nie na wspolnej liscie: ani odpowiedz serwera, ani zapis innej uwagi nie
 * podmieni tego, co recenzent wlasnie pisze, a pisanie nie przerysowuje transkryptu.
 */

const BEZ_PARAMETRU = "";

export function FormularzUwagi({ wpis, pozycja = null }: { wpis: WpisUwagi; pozycja?: PozycjaUwagi | null }): React.JSX.Element {
  const akcje = useAkcjePrzegladu();
  const { migawka, ponow } = useStatusZapisu();
  const id = useId();
  const refTekst = useRef<HTMLTextAreaElement>(null);
  const { klucz, numer, uwaga, szkic, usuwana } = wpis;

  // Szkic niezapisany w poprzedniej wizycie (zapis sie nie udal, a recenzent wyszedl z rozmowy) wygrywa z tym, co ma serwer.
  const [komentarz, setKomentarz] = useState(() => szkic?.komentarz ?? uwaga?.komentarz ?? "");
  const [parametr, setParametr] = useState<string>(() => (szkic ? szkic.parametr : (uwaga?.parametr ?? null)) ?? BEZ_PARAMETRU);
  const [konczy, setKonczy] = useState(false);

  // Formularz otwarty przez recenzenta przejmuje fokus (przywrocony z magazynu po wejsciu na rozmowe go nie zabiera).
  useEffect(() => {
    if (wpis.fokus) refTekst.current?.focus();
    // Efekt jednorazowy: pojawienie sie formularza.
  }, []);

  // Parametr zapisanej uwagi moze nie byc na liscie parametrow rozmowy (np. po zmianie metodyki): zostaje jako opcja,
  // zeby nie znikal po cichu.
  const opcje: readonly OpcjaSelect[] = [
    { wartosc: BEZ_PARAMETRU, etykieta: "Nie wybieram" },
    ...akcje.parametry.map((p) => ({ wartosc: p, etykieta: p })),
    ...(parametr !== BEZ_PARAMETRU && !akcje.parametry.includes(parametr) ? [{ wartosc: parametr, etykieta: parametr }] : []),
  ];

  const tresc = (k: string, p: string): { komentarz: string; parametr: string | null } => ({
    komentarz: k,
    parametr: p === BEZ_PARAMETRU ? null : p,
  });

  // Gdy usuniecie jest zakolejkowane, pole jest tylko do odczytu, ale zdarzenie moze jeszcze dojsc (np. wklejenie przez
  // narzedzia pomocnicze): zmiana nie moze ani trafic do kolejki, ani zmienic tego, co widzi recenzent.
  function zmienTekst(nowy: string): void {
    if (usuwana) return;
    setKomentarz(nowy);
    akcje.zapiszUwage(klucz, numer, tresc(nowy, parametr), OPOZNIENIE_TEKSTU_MS);
  }

  function zmienParametr(nowy: string): void {
    if (usuwana) return;
    setParametr(nowy);
    akcje.zapiszUwage(klucz, numer, tresc(komentarz, nowy), 0);
  }

  // Blur dowolnego pola formularza wysyla to, co czeka na przerwe w pisaniu. NIC WIECEJ: nie usuwa uwagi, takze gdy pole
  // jest puste, a fokus opuscil caly formularz (alt-tab, klik w puste miejsce, pasek adresu, okno potwierdzenia).
  function naBlur(): void {
    akcje.splusczUwage(klucz);
  }

  async function gotowe(): Promise<void> {
    if (konczy || usuwana) return;
    setKonczy(true);
    try {
      // Zamykanie robi hook po udanym zapisie. Przy bledzie formularz zostaje, a komunikat i ponowienie stoja obok.
      // Pusta tresc zapisanej uwagi otwiera okno potwierdzenia usuniecia, a nie usuwa od razu.
      await akcje.zakonczUwage(klucz, tresc(komentarz, parametr));
    } finally {
      setKonczy(false);
    }
  }

  return (
    <div
      className="rec-uwaga rec-uwaga--formularz"
      role="group"
      aria-labelledby={`${id}-tytul`}
      aria-busy={usuwana || undefined}
      data-klucz={klucz}
      onBlur={naBlur}
    >
      <p className="rec-uwaga__tytul" id={`${id}-tytul`}>
        {tytulUwagi(numer, pozycja)}
      </p>

      <Textarea
        ref={refTekst}
        etykieta="Co tu zadziałało, co nie, co model przeoczył? Możesz też wpisać swoją wersję wypowiedzi."
        id={`${id}-tekst`}
        maxLength={MAX_ZNAKOW_KOMENTARZA}
        value={komentarz}
        readOnly={usuwana}
        onChange={(e) => zmienTekst(e.target.value)}
      />

      <div className="rec-uwaga__wiersz">
        <Field etykieta="Czego dotyczy (nieobowiązkowe)" id={`${id}-parametr`}>
          {(atrybuty) => (
            <Select
              id={atrybuty.id}
              wartosc={parametr}
              opcje={opcje}
              onZmiana={zmienParametr}
              wylaczony={usuwana}
              aria-describedby={atrybuty["aria-describedby"]}
            />
          )}
        </Field>
      </div>

      <div className="rec-uwaga__akcje">
        {usuwana ? (
          <span className="rec-status-zapisu" data-stan="saving">
            <span className="rec-status-zapisu__napis" role="status">
              <Spinner rozmiar="s" />
              Usuwam...
            </span>
          </span>
        ) : (
          <StatusZapisu migawka={migawka} onPonow={ponow} ogloszenia={false} />
        )}
        {uwaga && (
          <Button wariant="danger" wylaczony={usuwana} onClick={() => akcje.zadajUsuniecieUwagi(uwaga)}>
            Usuń uwagę
          </Button>
        )}
        <Button wariant="primary" zajety={konczy} wylaczony={usuwana} onClick={() => void gotowe()}>
          Gotowe
        </Button>
      </div>
    </div>
  );
}
